import { useState } from "react";
import { Copy, Check, ExternalLink, Eye, EyeOff, ShieldCheck, Trash2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// What ScapeSource uses each Ramp permission for — shown in the tutorial
// so a novice knows what to tick and why.
// Keep in sync with backend/ramp.py's OAUTH_SCOPES.
const SCOPES = [
  ["transactions:read", "Read card charges", true],
  ["receipts:read", "Read receipts on those charges", true],
  ["accounting:read", "See Ramp's QuickBooks categories and jobs, and whether Ramp already posts to QuickBooks (avoids doubles)", false],
  ["accounting:write", "Code a charge (category and job) and mark it ready for Ramp's QuickBooks sync", false],
  ["transactions:write", "Tell a cardholder what's missing on a charge (a Ramp comment)", false],
  ["memos:write", "Write a charge's memo to Ramp", false],
  ["receipts:write", "Send a receipt to Ramp as the cardholder", false],
  ["users:read", "Import your Ramp users as ScapeSource purchasers", false],
  ["bills:read", "Bill Pay — see bills", false],
  ["bills:write", "Bill Pay — send invoices to Ramp to pay", false],
  ["vendors:read", "Bill Pay — find your vendors in Ramp", false],
  ["vendors:write", "Send your QuickBooks vendors to Ramp", false],
  ["entities:read", "Bill Pay — your Ramp business entity", false],
];
// What turns on without each group (the connect result names them).
const CODING_SCOPES = ["accounting:read", "accounting:write", "transactions:write", "memos:write", "receipts:write"];
const RAMP_HOME = { production: "https://app.ramp.com", sandbox: "https://demo.ramp.com" };

function CopyButton({ value, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — select it and copy by hand");
    }
  };
  return (
    <button type="button" onClick={copy} data-testid={testId} className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-[11.5px] font-semibold hover:bg-slate-50 shrink-0">
      {copied ? <Check size={11} /> : <Copy size={11} />} {copied ? "Copied" : label}
    </button>
  );
}

const Step = ({ n, title, children }) => (
  <div className="flex gap-3" data-testid={`ramp-guide-step-${n}`}>
    <div className="w-6 h-6 rounded-full bg-slate-900 text-white text-[12px] font-bold flex items-center justify-center shrink-0 mt-0.5">{n}</div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-[13px] text-slate-800">{title}</div>
      <div className="text-[12.5px] text-slate-600 mt-1 space-y-1.5">{children}</div>
    </div>
  </div>
);

// 2026-10-22 — a company connects Ramp with its OWN Ramp app (Client
// Credentials). The Client ID and Secret are encrypted the moment they're
// saved and are never shown again; to change them, delete and add again.
export function RampOwnAppConnect({ onConnected }) {
  const [open, setOpen] = useState(false);
  const [env, setEnv] = useState("production");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  // Where Ramp would send a "Connect with Ramp" sign-in back to. Only that
  // sign-in uses it; Ramp's form may still ask for one.
  const apiBase = /^https?:\/\//.test(API) ? API : `${window.location.origin}${API.replace(/^undefined/, "")}`;
  const redirectUrl = `${apiBase}/contractor/integrations/ramp/callback`;
  const allScopes = SCOPES.map(([s]) => s).join(" ");

  const close = () => {
    setOpen(false); setClientId(""); setClientSecret(""); setShowSecret(false); setResult(null);
  };

  const save = async () => {
    if (!clientId.trim() || !clientSecret.trim()) return toast.error("Paste both the Client ID and the Client Secret");
    setSaving(true);
    try {
      const { data } = await api.post("/contractor/integrations/ramp/credentials", { client_id: clientId, client_secret: clientSecret, environment: env });
      setClientId(""); setClientSecret(""); // never kept in the page
      setResult(data);
      toast.success("Ramp connected");
      onConnected?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ramp didn't accept those credentials", { duration: 9000 });
    } finally {
      setSaving(false);
    }
  };

  const missing = result ? SCOPES.filter(([s]) => !result.scopes.includes(s)) : [];
  const codingOff = result ? !CODING_SCOPES.every((s) => result.scopes.includes(s)) : false;
  const usersOff = result ? !result.scopes.includes("users:read") : false;

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ramp-own-app-button"
        className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-700 text-white text-[12.5px] font-semibold whitespace-nowrap inline-flex items-center gap-1.5">
        <KeyRound size={13} /> Connect with your Ramp app
      </button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ramp-own-app-modal">
          <DialogHeader><DialogTitle>Connect Ramp — step by step</DialogTitle></DialogHeader>

          {result ? (
            <div className="space-y-3 text-[13px]" data-testid="ramp-own-app-done">
              <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3.5 py-3 text-emerald-900">
                <div className="font-semibold">Ramp is connected{result.environment === "sandbox" ? " (sandbox)" : ""}.</div>
                <div className="text-[12.5px] mt-1">Your Ramp app (Client ID ending ••••{result.client_id_hint}) is saved encrypted. It can't be viewed again — to change it, delete it and add a new one.</div>
              </div>
              {missing.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-100 px-3.5 py-3 text-[12.5px] text-amber-900" data-testid="ramp-missing-scopes">
                  Your Ramp app didn't allow these, so these parts stay off: {missing.map(([s, what]) => `${what} (${s})`).join("; ")}.
                  {(codingOff || usersOff) && (
                    <span className="block mt-1.5 font-semibold">
                      {codingOff && "Coding charges here (category, job, memo, receipt, mark ready) is off. "}
                      {usersOff && "Importing your Ramp users is off. "}
                    </span>
                  )}
                  To turn them on, tick them on the app in Ramp (Company → Developer → your app → Configure allowed scopes), then delete the Ramp credentials here and add them again.
                </div>
              )}
              <div className="text-[12.5px] text-slate-600">Next: go to <b>Ramp Transactions</b> and click <b>Sync from Ramp</b> to bring in your card charges.</div>
              <button onClick={close} className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold">Done</button>
            </div>
          ) : (
            <div className="space-y-4 mt-1">
              <p className="text-[12.5px] text-slate-500 -mt-1">
                About 5 minutes. You create a small "app" inside your own Ramp account, then paste its two keys here. ScapeSource only ever sees your company's Ramp data.
              </p>

              <Step n={1} title="Use a Ramp admin login">
                <p>Only a Ramp <b>Admin</b> or <b>Business Owner</b> can create the app. If that's not you, send this page to your Ramp admin.</p>
              </Step>

              <Step n={2} title="Pick which Ramp you use">
                <div className="flex gap-2">
                  {[["production", "My company's Ramp"], ["sandbox", "Ramp sandbox (testing)"]].map(([v, label]) => (
                    <button key={v} type="button" onClick={() => setEnv(v)} data-testid={`ramp-env-${v}`}
                      className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold border ${env === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 hover:bg-slate-50"}`}>{label}</button>
                  ))}
                </div>
              </Step>

              <Step n={3} title="Open the Developer page in Ramp">
                <p>Sign in to Ramp, then in the left menu click <b>Company</b> → <b>Developer</b> → <b>Create New App</b>.</p>
                <a href={RAMP_HOME[env]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-600 font-semibold">
                  Open {env === "sandbox" ? "Ramp sandbox" : "Ramp"} <ExternalLink size={11} />
                </a>
                <p>Name the app <b>ScapeSource</b> and accept Ramp's terms.</p>
                <div className="flex items-center gap-2"><code className="px-2 py-1 rounded bg-slate-100 text-[12px]">ScapeSource</code><CopyButton value="ScapeSource" testId="copy-app-name" /></div>
              </Step>

              <Step n={4} title={'Add the "Client Credentials" grant type'}>
                <p>On the app, under <b>Grant types</b>, click <b>Add new grant type</b> and choose <b>Client Credentials</b>.</p>
              </Step>

              <Step n={5} title="Allow these permissions (scopes)">
                <p>
                  Under <b>Scopes</b>, click <b>Configure allowed scopes</b> and tick each one below — or use <b>Enable all</b> if Ramp offers it. The first two
                  are required to bring in card charges. The next six let you <b>code a charge here</b> (category, job, memo, receipt, mark ready) and
                  <b>import your Ramp users</b>; the rest turn on Bill Pay and vendor features. A scope you skip just leaves that feature off.
                </p>
                <div className="border border-slate-100 rounded-lg divide-y divide-slate-50">
                  {SCOPES.map(([scope, what, required]) => (
                    <div key={scope} className="flex items-center justify-between gap-2 px-2.5 py-1.5">
                      <div className="min-w-0"><code className="text-[12px]">{scope}</code> <span className="text-[11.5px] text-slate-400">— {what}{required ? " (required)" : ""}</span></div>
                      <CopyButton value={scope} testId={`copy-scope-${scope}`} />
                    </div>
                  ))}
                </div>
                <div><CopyButton value={allScopes} label="Copy all scopes" testId="copy-all-scopes" /></div>
              </Step>

              <Step n={6} title="If Ramp asks for a Redirect URL, paste this">
                <p>This way of connecting doesn't use it, but Ramp's form may ask for one. Paste exactly:</p>
                <div className="flex items-center gap-2">
                  <code className="px-2 py-1 rounded bg-slate-100 text-[11.5px] break-all" data-testid="ramp-redirect-url">{redirectUrl}</code>
                  <CopyButton value={redirectUrl} testId="copy-redirect-url" />
                </div>
              </Step>

              <Step n={7} title="Copy the Client ID and Client Secret, and paste them here">
                <p>Ramp shows them on the app page. Click <b>Save</b> in Ramp first if it asks.</p>
                <div className="space-y-2">
                  <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="Client ID" autoComplete="off" spellCheck={false}
                    data-testid="ramp-client-id-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm mono" />
                  <div className="relative">
                    <input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Client Secret" autoComplete="off" spellCheck={false}
                      type={showSecret ? "text" : "password"} data-testid="ramp-client-secret-input" className="w-full h-10 pl-3 pr-10 rounded-lg border border-slate-200 text-sm mono" />
                    <button type="button" onClick={() => setShowSecret((v) => !v)} title={showSecret ? "Hide" : "Show while typing"}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">{showSecret ? <EyeOff size={15} /> : <Eye size={15} />}</button>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2 text-[12px] text-slate-600">
                  <ShieldCheck size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                  <span>Encrypted the moment you save, and <b>never shown again</b> — not to you, not to anyone. To change them later, delete the Ramp credentials here and add new ones.</span>
                </div>
              </Step>

              <button disabled={saving} onClick={save} data-testid="ramp-own-app-save" className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold disabled:opacity-60">
                {saving ? "Checking with Ramp…" : "Save and connect"}
              </button>
              <p className="text-[11.5px] text-slate-400 text-center">ScapeSource checks the keys with Ramp before saving and tells you which features they allow.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// Connected with the company's own Ramp app: shows which app (last 4 of the
// Client ID only) and the one way to change it — delete permanently.
export function RampOwnAppDelete({ clientIdHint, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/contractor/integrations/ramp/credentials");
      toast.success("Ramp credentials permanently deleted");
      setOpen(false); setTyped("");
      onDeleted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete the Ramp credentials");
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ramp-delete-credentials-button"
        className="px-3 py-1.5 rounded-lg border border-red-200 text-red-700 text-[12px] font-semibold hover:bg-red-50 inline-flex items-center gap-1">
        <Trash2 size={12} /> Delete Ramp credentials
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setTyped(""); }}>
        <DialogContent data-testid="ramp-delete-credentials-modal">
          <DialogHeader><DialogTitle>Permanently delete Ramp credentials?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-[13px] text-slate-600">
            <p>This erases your Ramp app's Client ID{clientIdHint ? ` (ending ••••${clientIdHint})` : ""} and Secret from ScapeSource for good, and disconnects Ramp. Charges already brought in stay.</p>
            <p>To connect again, you'll paste the Client ID and Secret again (from Ramp → Company → Developer).</p>
            <p>Type <b>DELETE</b> to confirm.</p>
            <input value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="ramp-delete-confirm-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <button disabled={busy || typed !== "DELETE"} onClick={remove} data-testid="ramp-delete-credentials-confirm"
              className="w-full h-10 rounded-lg bg-red-600 text-white text-[13px] font-semibold disabled:opacity-50">
              {busy ? "Deleting…" : "Permanently delete"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
